import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';
import type { Orientation } from './controller';

/*
 * WHERE THE READER IS, NAMED — the pill at the top of the map.
 *
 * The map's positions carry two claims: how near the divine a figure stands, and which texts name
 * it. Both are drawn, and neither is legible until something says them in words. The ladder gives
 * a reader a scale to read a radius against; this gives them the words.
 *
 * IT READS THE FIGURE IN FRONT OF THEM, NOT THE CAMERA. The camera has a radius and the radius has
 * a rung, which is the obvious implementation and the wrong one: a camera sitting in the gap
 * between two rungs has a number and no answer, and a camera centred on empty sky would name a
 * rung nobody is looking at. The honest answer is a figure — the one they opened, or the nearest
 * one to the middle of the view — and what rung THAT stands on.
 */

/** The rungs, in the order the bundle numbers them. */
const TIER_NAME_KEYS = ['tier.0.name', 'tier.1.name', 'tier.2.name', 'tier.3.name', 'tier.4.name'] as const;

/**
 * THE INNER RUNGS ARE NAMED ALONE.
 *
 * A name of God or an angel belongs to the rung before it belongs to any one scripture — saying
 * "HEBREW BIBLE · THE NAMES OF GOD" would offer the wrong half as the headline, and most of them
 * are named by several texts anyway. Further out, a figure only one text names is worth siting:
 * "QURAN · NAMED" says something "NAMED" alone does not.
 */
const RUNGS_NAMED_ALONE = 2;

export interface MapOrientationProps {
  /** The figure in front of the reader, or null when the map holds none. */
  at: Orientation | null;
  /** Corpus id → what to call it and what colour it wears. */
  corpora: readonly { id: string; name: string; colour: string }[];
  /**
   * Open the travel list. Theirs hangs it off this element (universe-v8.dc.html:114).
   *
   * Absent leaves the pill a plain label, which is what a caller with nowhere to travel to wants —
   * and keeps it honest that naming where the reader is standing is its first job.
   */
  onOpen?: () => void;
}

/**
 * The pill's text and colour — exported so a test can assert the RULE rather than the markup.
 *
 * `t` is the caller's translator, so the phrase arrives in the reader's language: the rung names
 * live in the dictionary and never in the bundle, because a rung is read aloud to a person and
 * the bundle would hand a Hebrew reader English.
 */
export function orientationLabel(
  at: Orientation | null,
  corpora: readonly { id: string; name: string; colour: string }[],
  t: (key: keyof Strings) => string,
): { text: string; colour: string | undefined } {
  if (!at) return { text: t('tier.unknown'), colour: undefined };
  const key = TIER_NAME_KEYS[at.tier] ?? 'tier.3.name';
  const rung = t(key);
  if (at.tier < RUNGS_NAMED_ALONE) return { text: rung, colour: undefined };

  /* Only a figure exactly one text names is sited by that text. A figure several name belongs to
     no one sector, and prefixing it with any of them would pick a side the map does not. */
  const only = at.corpora.length === 1 ? corpora.find(c => c.id === at.corpora[0]) : undefined;
  return only
    ? { text: `${only.name.toUpperCase()} · ${rung}`, colour: only.colour }
    : { text: rung, colour: undefined };
}

export function MapOrientation({ at, corpora, onOpen }: MapOrientationProps): React.JSX.Element {
  const { t } = useLang();
  const { text, colour } = orientationLabel(at, corpora, t);
  const style = colour ? { color: colour } : undefined;
  /*
   * Live, but polite and atomic: it changes as the reader moves and must never interrupt them
   * mid-sentence, and half of "QURAN · NAMED" read on its own says the wrong thing.
   */
  const said = { 'aria-live': 'polite', 'aria-atomic': true } as const;

  /*
   * IT IS A CONTROL WHEN THERE IS SOMEWHERE TO GO. Theirs opens the travel list from this exact
   * element (universe-v8.dc.html:114) and ours was inert — a label in the right place doing none of
   * the work its twin does. Without the callback it stays a paragraph, which is what the old
   * renderer wants and what keeps this component honest about being a label first.
   */
  return onOpen === undefined
    ? <p className="map-orientation" data-map-chrome {...said} style={style}>{text}</p>
    : (
      <button
        type="button"
        className="map-orientation map-orientation--act"
        data-map-chrome
        {...said}
        style={style}
        onClick={onOpen}
        title={t('travel.title')}
      >
        {text}
      </button>
    );
}
